import { Skeleton, Tooltip } from '@heroui/react';

import { CopyableText } from '~/components/CopyableText';
import { useTruncatedTooltip } from '~/components/TruncatedText';

type PipelineNameCellProps = {
  pipelineName: string;
  pipelineVersion: string;
};

function parsePipelineName(pipelineName: string): {
  name: string;
  version: string;
} {
  const versionMatch = pipelineName.match(/[-.]v[\d.]+$/i);
  if (versionMatch) {
    const versionIndex = versionMatch.index ?? pipelineName.length;

    return {
      name: pipelineName.slice(0, versionIndex),
      version: pipelineName.slice(versionIndex + 1)
    };
  }
  return { name: pipelineName, version: '' };
}

export function PipelineNameCell({
  pipelineName,
  pipelineVersion
}: PipelineNameCellProps) {
  // TODO: ADJUST AFTER monitor_migrate_pipeline_logs_to_monitor_service IS PERMANENT - use pipelineName and pipelineVersion directly
  const parsed = parsePipelineName(pipelineName);
  const version = parsed.version || '';

  const { ref, isTruncated } = useTruncatedTooltip(parsed.name);

  if (!parsed.name) return <span>-</span>;

  return (
    <CopyableText
      text={parsed.name}
      className='whitespace-normal text-left lg:min-w-[142px] xl:min-w-[172px] 3xl:min-w-[272px] justify-start'
      data-testid='monitor-pipeline-logs-cell-pipeline-name'
    >
      <Tooltip isDisabled={!isTruncated} content={parsed.name}>
        <div className='flex items-center gap-2'>
          <span
            ref={ref as React.RefObject<HTMLSpanElement>}
            className='line-clamp-1 break-all text-title-sm'
          >
            {parsed.name}
          </span>
          <span className='rounded-[4px] bg-surface-dim px-1 py-0.5 text-label-md'>
            {pipelineVersion || version}
          </span>
        </div>
      </Tooltip>
    </CopyableText>
  );
}

export function PipelineNameCellSkeleton() {
  return (
    <div className='flex items-center gap-2'>
      <Skeleton className='h-5 lg:w-[88px] xl:w-[118px] 3xl:w-[218px] rounded' />
      <Skeleton className='h-5 w-[30px] rounded-[4px]' />
    </div>
  );
}
